import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import IconButton from '@mui/material/IconButton';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import LabelIcon from '@mui/icons-material/Label';
import MailIcon from '@mui/icons-material/Mail';
import SearchIcon from '@mui/icons-material/Search';
import type { ActiveFilter, CategoryFilter, TimeFilterType } from '../types';
import { CATEGORIES, TypeFilter } from '../TypeFilter/TypeFilter';
import { TIME_OPTIONS, TimeFilter } from '../TimeFilter/TimeFilter';
import { LabelFilter } from '../LabelFilter/LabelFilter';
import { FilterChip } from '../FilterChip/FilterChip';

export interface CommunicationsToolbarProps {
  searchQuery: string;
  onSearchChange: (v: string) => void;
  activeFilter: ActiveFilter;
  onFilterChange: (filter: ActiveFilter) => void;
  activeTime: TimeFilterType;
  onTimeChange: (t: TimeFilterType) => void;
  availableLabels: string[];
  selectedLabels: string[];
  onLabelsChange: (labels: string[]) => void;
  selectedCategories: CategoryFilter[];
  onCategoryChange: (categories: CategoryFilter[]) => void;
}

const ACTIVE_CHIP_SX = {
  '& .MuiChip-icon': { ml: '10px' },
  '& .MuiChip-deleteIcon': {
    color: 'text.secondary',
    '&:hover': { color: 'text.primary' },
  },
} as const;

export const CommunicationsToolbar = ({
  searchQuery,
  onSearchChange,
  activeFilter,
  onFilterChange,
  activeTime,
  onTimeChange,
  availableLabels,
  selectedLabels,
  onLabelsChange,
  selectedCategories,
  onCategoryChange,
}: Readonly<CommunicationsToolbarProps>) => {
  const timeLabel =
    TIME_OPTIONS.find(o => o.value === activeTime)?.label ?? 'Time';
  const timeActive = activeTime !== 'all';

  const anyFilterActive =
    activeFilter === 'unread' ||
    timeActive ||
    selectedCategories.length > 0 ||
    selectedLabels.length > 0;

  const toggleCategory = (cat: CategoryFilter) =>
    onCategoryChange(
      selectedCategories.includes(cat)
        ? selectedCategories.filter(c => c !== cat)
        : [...selectedCategories, cat],
    );

  const toggleLabel = (label: string) =>
    onLabelsChange(
      selectedLabels.includes(label)
        ? selectedLabels.filter(l => l !== label)
        : [...selectedLabels, label],
    );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Row 1: search + filter chips */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <OutlinedInput
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Search"
          size="small"
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          }
          endAdornment={
            searchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onSearchChange('')}
                  edge="end"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }
          sx={{ width: '40%', minWidth: 180, borderRadius: 6 }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TypeFilter
            selectedCategories={selectedCategories}
            onCategoryChange={onCategoryChange}
          />

          <FilterChip
            label="Unread"
            icon={<MailIcon sx={{ fontSize: 18 }} />}
            active={activeFilter === 'unread'}
            tooltip={
              activeFilter === 'unread'
                ? 'Showing unread — click to clear'
                : 'Show unread'
            }
            onClick={() =>
              onFilterChange(activeFilter === 'unread' ? 'all' : 'unread')
            }
            width={120}
            showArrow={false}
          />

          <TimeFilter activeTime={activeTime} onTimeChange={onTimeChange} />

          <LabelFilter
            availableLabels={availableLabels}
            selectedLabels={selectedLabels}
            onLabelsChange={onLabelsChange}
          />
        </Box>
      </Box>

      {/* Row 2: active filter chips — always rendered to avoid layout shift */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 28 }}
      >
        {anyFilterActive && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                maxWidth: '90%',
              }}
            >
              {activeFilter === 'unread' && (
                <Chip
                  label="Unread"
                  icon={<MailIcon />}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onDelete={() => onFilterChange('all')}
                  sx={{
                    '& .MuiChip-icon': { ml: '10px' },
                    '& .MuiChip-deleteIcon': {
                      color: 'white',
                      '&:hover': { color: 'text.primary' },
                    },
                  }}
                />
              )}
              {selectedCategories.map(cat => {
                const catDef = CATEGORIES.find(c => c.value === cat);
                return (
                  <Chip
                    key={cat}
                    label={catDef?.label ?? cat}
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onDelete={() => toggleCategory(cat)}
                    sx={{
                      '& .MuiChip-deleteIcon': {
                        color: 'text.secondary',
                        '&:hover': { color: 'text.primary' },
                      },
                    }}
                  />
                );
              })}
              {timeActive && (
                <Chip
                  label={timeLabel}
                  icon={<AccessTimeIcon />}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onDelete={() => onTimeChange('all')}
                  sx={ACTIVE_CHIP_SX}
                />
              )}
              {selectedLabels.map(label => (
                <Chip
                  key={label}
                  label={label}
                  icon={<LabelIcon />}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onDelete={() => toggleLabel(label)}
                  sx={ACTIVE_CHIP_SX}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
